import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/ChecklistSettingsDashboard/components/ChecklistTemplatesTable/ChecklistTemplatesTable.scss";
import { useChecklistTemplatesQuery, useReportTemplatesQuery } from "modules/ChecklistSettingsDashboard/components/ChecklistTemplatesTable/hooks";
import { DeleteChecklistTemplatesModal } from "modules/ChecklistSettingsDashboard/components/DeleteChecklistTemplateModal";
import { TemplateModal } from "modules/ChecklistSettingsDashboard/components/TemplateModal";

import type { ChecklistTemplateShort } from "models";

const columnHelper = createColumnHelper<ChecklistTemplateShort>();

export const ChecklistTemplatesTable = () => {
  const { t } = useTranslation();

  const { data: checklistTemplates, isFetching: isLoadingChecklistTemplates, refetch: refetchChecklistTemplates } = useChecklistTemplatesQuery();
  const { data: reportTemplateOptions = [] } = useReportTemplatesQuery();
  useRegisterNavAction(() => refetchChecklistTemplates());

  const [templateToUpdateID, setTemplateToUpdateID] = useState<number | null>(null);
  const [templateToDeleteID, setTemplateToDeleteID] = useState<number | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => <span>{t("v8_name", "Name")}</span>,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 60,
        cell: ({ row: { original: template } }) => {
          return (
            <div className="actions-container">
              <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.ChecklistTemplates}>
                <div className="action-edit-button-container" onClick={() => setTemplateToUpdateID(template.checklist_template_id)}>
                  <Icon className="icon pen to square edit-icon action-button-icon-center" />
                </div>
              </Can>
              <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.ChecklistTemplates}>
                <div className="action-button-container" onClick={() => setTemplateToDeleteID(template.checklist_template_id)}>
                  <Icon className="trash action-button-icon-center" color="red" />
                </div>
              </Can>
            </div>
          );
        }
      })
    ],
    [t]
  );

  return (
    <>
      <div className="ChecklistTemplatesTable">
        <ReactTable columns={columns} data={checklistTemplates} isFetching={isLoadingChecklistTemplates} />
      </div>
      <TemplateModal
        isOpen={templateToUpdateID !== null}
        templateID={templateToUpdateID}
        reportTemplateOptions={reportTemplateOptions}
        onClose={() => setTemplateToUpdateID(null)}
      />
      <DeleteChecklistTemplatesModal templateID={templateToDeleteID} onClose={() => setTemplateToDeleteID(null)} />
    </>
  );
};
